/**
 * MC Toki: a tiny, defensive wrapper around the Toki loyalty app embed.
 *
 *   window.MCToki.ready         Promise<object|null>  resolves to window.tokiWallet, or null after ~15s
 *   window.MCToki.getBalance()  Promise<{ points, name, value } | null>
 *       points: whole points (number), name: Toki's label ("Points"),
 *       value: store credit in major units of the shop currency (points / conversionRate),
 *       or null when Toki gives no conversion rate
 *   window.MCToki.refresh()     same, bypassing the 5 minute cache
 *   window.MCToki.formatPoints(n) / formatMoney(value)  en-GB number and shop-currency money
 *
 * Verified live (19 Oct 2026): tokiWallet.getUserBalance() resolves to
 *   { balance: 1178, name: "Points", conversionRate: 100, storeCreditId, rewardDisplayPreference, ... }
 * and throws "Unauthorized" without a customer token. Other shapes are read defensively.
 *
 * Never throws. Returns null when nobody is logged in, Toki never loads, the API rejects
 * the request, or the response holds nothing numeric. Results are cached in sessionStorage
 * for 5 minutes per customer. When a balance resolves, `mc:toki:balance` is dispatched on
 * document with { detail: { points, name, value } }.
 *
 * Load with a classic script tag (customer id blank when logged out):
 *   <script src="{{ 'mc-toki.js' | asset_url }}" data-customer-id="{{ customer.id }}"
 *     data-currency="{{ shop.currency }}" defer></script>
 */
(function () {
  if (window.MCToki) return;

  var WAIT_MS = 15000;
  // Right after sign-in Toki has no customer token yet and answers "Unauthorized" for a few
  // seconds, so retry for about 20s before giving up.
  var RETRY_MS = [500, 1000, 2000, 3000, 4000, 4500, 5000];
  var CALL_TIMEOUT_MS = 6000;
  var CACHE_MS = 5 * 60 * 1000;
  var script = document.currentScript;
  var customerId = (script && script.getAttribute('data-customer-id')) || '';
  var currency = (script && script.getAttribute('data-currency')) || 'GBP';
  var cacheKey = 'mc-toki-balance-v2:' + customerId;
  var debug = /[?&]mc_toki_debug=1/.test(location.search);

  function log() {
    if (!debug) return;
    try {
      console.log.apply(console, ['[mc-toki]'].concat([].slice.call(arguments)));
    } catch (e) {}
  }

  function withTimeout(promise, ms) {
    return new Promise(function (resolve, reject) {
      var timer = setTimeout(function () {
        reject(new Error('timeout'));
      }, ms);
      Promise.resolve(promise).then(
        function (value) {
          clearTimeout(timer);
          resolve(value);
        },
        function (error) {
          clearTimeout(timer);
          reject(error);
        }
      );
    });
  }

  var ready = new Promise(function (resolve) {
    if (!customerId) {
      resolve(null);
      return;
    }
    var started = Date.now();
    (function poll() {
      var wallet = window.tokiWallet;
      if (wallet && typeof wallet === 'object') {
        resolve(wallet);
        return;
      }
      if (Date.now() - started > WAIT_MS) {
        log('tokiWallet not found after', WAIT_MS, 'ms');
        resolve(null);
        return;
      }
      setTimeout(poll, 200);
    })();
  });

  var NUMERIC_KEYS = [
    'balance',
    'points',
    'availablePoints',
    'available_points',
    'pointsBalance',
    'points_balance',
    'point_balance',
    'currentPoints',
    'current_points',
    'total',
    'totalPoints',
    'total_points',
    'amount',
    'value',
  ];
  var NESTED_KEYS = ['data', 'result', 'wallet', 'user', 'balance', 'points', 'customer', 'member'];

  /**
   * Pull a points number out of an unknown response shape.
   * @param {unknown} value
   * @param {number} depth
   * @returns {number|null}
   */
  function extractNumber(value, depth) {
    if (value == null || depth > 3) return null;
    if (typeof value === 'number') return isFinite(value) && value >= 0 ? value : null;
    if (typeof value === 'string') {
      var cleaned = value.replace(/[,\s]/g, '');
      if (/^\d+(\.\d+)?$/.test(cleaned)) return Number(cleaned);
      return null;
    }
    if (typeof value !== 'object') return null;

    var i;
    for (i = 0; i < NUMERIC_KEYS.length; i++) {
      var key = NUMERIC_KEYS[i];
      if (Object.prototype.hasOwnProperty.call(value, key)) {
        var direct = value[key];
        if (typeof direct === 'number' || typeof direct === 'string') {
          var n = extractNumber(direct, depth + 1);
          if (n !== null) return n;
        }
      }
    }
    for (i = 0; i < NESTED_KEYS.length; i++) {
      var nested = value[NESTED_KEYS[i]];
      if (nested && typeof nested === 'object') {
        var m = extractNumber(nested, depth + 1);
        if (m !== null) return m;
      }
    }
    return null;
  }

  function readCache() {
    try {
      var raw = sessionStorage.getItem(cacheKey);
      if (!raw) return null;
      var parsed = JSON.parse(raw);
      if (parsed && parsed.v && typeof parsed.v.points === 'number' && Date.now() - parsed.t < CACHE_MS) return parsed.v;
    } catch (e) {}
    return null;
  }

  function writeCache(balance) {
    try {
      sessionStorage.setItem(cacheKey, JSON.stringify({ v: balance, t: Date.now() }));
    } catch (e) {}
  }

  async function callWallet(wallet, name, arg) {
    if (typeof wallet[name] !== 'function') return null;
    try {
      var response = await withTimeout(arg === undefined ? wallet[name]() : wallet[name](arg), CALL_TIMEOUT_MS);
      log(name, response);
      return response;
    } catch (error) {
      log(name, 'failed', error && error.message);
      return null;
    }
  }

  /**
   * @param {unknown} response
   * @returns {{ points: number, name: string, value: number | null } | null}
   */
  function toResult(response) {
    var points = extractNumber(response, 0);
    if (points === null) return null;
    points = Math.round(points);
    var name = 'Points';
    var value = null;
    if (response && typeof response === 'object') {
      if (typeof response.name === 'string' && response.name.trim()) name = response.name.trim();
      var rate = Number(response.conversionRate);
      if (isFinite(rate) && rate > 0) value = Math.round((points / rate) * 100) / 100;
    }
    return { points: points, name: name, value: value };
  }

  async function fetchBalance(wallet) {
    var result = toResult(await callWallet(wallet, 'getUserBalance'));
    if (result) return result;

    var shopDomain = (wallet.config && wallet.config.shopDomain) || (window.Shopify && window.Shopify.shop) || '';
    return toResult(await callWallet(wallet, 'toki_get_user_data', { shopDomain: shopDomain }));
  }

  /** @type {Promise<{ points: number, name: string, value: number | null } | null> | null} */
  var inflight = null;

  function load(force) {
    if (!customerId) return Promise.resolve(null);
    if (!force) {
      var cached = readCache();
      if (cached !== null) return Promise.resolve(cached);
    }
    if (inflight) return inflight;

    inflight = (async function () {
      try {
        var wallet = await ready;
        if (!wallet) return null;

        var balance = await fetchBalance(wallet);
        for (var attempt = 0; balance === null && attempt < RETRY_MS.length; attempt++) {
          await new Promise(function (r) {
            setTimeout(r, RETRY_MS[attempt]);
          });
          balance = await fetchBalance(wallet);
        }

        if (balance !== null) {
          writeCache(balance);
          try {
            document.dispatchEvent(new CustomEvent('mc:toki:balance', { detail: balance }));
          } catch (e) {}
        }
        return balance;
      } catch (error) {
        log('unexpected', error);
        return null;
      } finally {
        inflight = null;
      }
    })();
    return inflight;
  }

  window.MCToki = {
    ready: ready,
    loggedIn: Boolean(customerId),
    getBalance: function () {
      return load(false);
    },
    refresh: function () {
      return load(true);
    },
    formatPoints: function (points) {
      try {
        return new Intl.NumberFormat('en-GB').format(points);
      } catch (e) {
        return String(points);
      }
    },
    formatMoney: function (value) {
      try {
        return new Intl.NumberFormat('en-GB', { style: 'currency', currency: currency }).format(value);
      } catch (e) {
        return String(value);
      }
    },
  };

  /**
   * Fill every [data-mc-toki-points] element. It keeps its skeleton until the balance
   * resolves and is hidden when there is none. Inside it:
   *   [data-mc-toki-value]  the formatted points, e.g. 1,178
   *   [data-mc-toki-name]   Toki's label, lower-cased for running text ("points")
   *   [data-mc-toki-money]  the store credit, e.g. £11.78; its [data-mc-toki-money-wrap]
   *                         ancestor is hidden when Toki gives no conversion rate
   */
  function hydrate() {
    var nodes = document.querySelectorAll('[data-mc-toki-points]');
    if (!nodes.length) return;
    load(false).then(function (balance) {
      var all = document.querySelectorAll('[data-mc-toki-points]');
      for (var i = 0; i < all.length; i++) {
        var node = all[i];
        if (balance === null) {
          node.hidden = true;
          node.setAttribute('data-mc-toki-state', 'empty');
          continue;
        }
        var valueEl = node.querySelector('[data-mc-toki-value]');
        if (valueEl) valueEl.textContent = window.MCToki.formatPoints(balance.points);
        var nameEls = node.querySelectorAll('[data-mc-toki-name]');
        for (var j = 0; j < nameEls.length; j++) nameEls[j].textContent = balance.name.toLowerCase();
        var moneyEls = node.querySelectorAll('[data-mc-toki-money]');
        for (var k = 0; k < moneyEls.length; k++) {
          var wrap = moneyEls[k].closest('[data-mc-toki-money-wrap]') || moneyEls[k];
          if (balance.value === null || balance.value <= 0) {
            wrap.hidden = true;
          } else {
            moneyEls[k].textContent = window.MCToki.formatMoney(balance.value);
            wrap.hidden = false;
          }
        }
        node.hidden = false;
        node.setAttribute('data-mc-toki-state', 'ready');
        node.removeAttribute('aria-busy');
      }
    });
  }

  window.MCToki.hydrate = hydrate;

  // Toki announces its token and balance changes on window. A late token fills a header that
  // gave up; a redeem updates the shown balance.
  if (customerId) {
    ['tokiWalletUserAuthenticated', 'tokiWalletRpcInjected'].forEach(function (name) {
      window.addEventListener(name, function () {
        if (readCache() === null) load(true).then(hydrate);
      });
    });
    window.addEventListener('tokiWalletBalanceChanged', function () {
      load(true).then(hydrate);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', hydrate);
  } else {
    hydrate();
  }
})();
